import { formatDateMedium, parseValidDate } from "@dofek/format/format";
import { userFacingErrorMessage } from "@dofek/format/user-facing-error";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useState } from "react";
import { AccountErasurePanel } from "../components/AccountErasurePanel.tsx";
import { ClimbingGradeSystemToggle } from "../components/ClimbingGradeSystemToggle.tsx";
import { DataSourcesPanel } from "../components/DataSourcesPanel.tsx";
import { ExportPanel } from "../components/ExportPanel.tsx";
import { LinkedAccountsPanel } from "../components/LinkedAccountsPanel.tsx";
import { MedicationDoseEventsPanel } from "../components/MedicationDoseEventsPanel.tsx";
import { PageLayout } from "../components/PageLayout.tsx";
import { PageSection } from "../components/PageSection.tsx";
import { PasswordSettingsPanel } from "../components/PasswordSettingsPanel.tsx";
import { PersonalizationPanel } from "../components/PersonalizationPanel.tsx";
import { UnitSystemToggle } from "../components/UnitSystemToggle.tsx";
import { ZeppPairingPanel } from "../components/ZeppPairingPanel.tsx";
import {
  clearWebBillingCheckoutOperation,
  getOrCreateWebBillingCheckoutOperationId,
} from "../lib/billing-checkout-operation.ts";
import { SECTION_LABELS, useDashboardLayout } from "../lib/dashboardLayoutContext.ts";
import { captureException } from "../lib/telemetry.ts";
import { trpc } from "../lib/trpc.ts";
import { McpTokensPanel } from "./McpTokensPanel.tsx";
import {
  normalizeSettingsCategory,
  SETTINGS_CATEGORIES,
  type SettingsCategory,
} from "./settingsCategories.ts";

function getRecentWeekLabel(startDate: string, endDateExclusive: string): string {
  const endInclusive = parseValidDate(`${endDateExclusive}T12:00:00.000Z`);
  if (!endInclusive) return `${formatDateMedium(startDate)} to --`;
  endInclusive.setUTCDate(endInclusive.getUTCDate() - 1);

  return `${formatDateMedium(startDate)} to ${formatDateMedium(endInclusive)}`;
}

const billingRegionClassName = "min-h-44 sm:min-h-32 lg:min-h-28";

export function SettingsPage() {
  const search = useSearch({ from: "/settings" });
  const navigate = useNavigate({ from: "/settings" });
  const requestedCategory: SettingsCategory = normalizeSettingsCategory(search.tab) ?? "account";
  const [categorySearch, setCategorySearch] = useState("");
  const normalizedCategorySearch = categorySearch.trim().toLowerCase();
  const visibleCategories = SETTINGS_CATEGORIES.filter(
    (category) =>
      normalizedCategorySearch.length === 0 ||
      `${category.label} ${category.searchText}`.toLowerCase().includes(normalizedCategorySearch),
  );
  const activeCategory =
    visibleCategories.find((category) => category.id === requestedCategory)?.id ??
    visibleCategories[0]?.id ??
    null;
  const { layout, toggleHidden, resetLayout } = useDashboardLayout();
  const [checkoutClientError, setCheckoutClientError] = useState<string | null>(null);
  const billingStatus = trpc.billing.status.useQuery();
  const checkoutSessionMutation = trpc.billing.createCheckoutSession.useMutation({
    onSuccess: ({ url }, { operationId }) => {
      try {
        clearWebBillingCheckoutOperation(operationId);
      } catch (error: unknown) {
        captureException(error, { source: "billing-checkout-operation-clear" });
      }
      window.location.assign(url);
    },
  });
  const portalSessionMutation = trpc.billing.createPortalSession.useMutation({
    onSuccess: ({ url }) => {
      window.location.assign(url);
    },
  });
  function handleCheckout(): void {
    setCheckoutClientError(null);
    try {
      const operationId = getOrCreateWebBillingCheckoutOperationId();
      checkoutSessionMutation.mutate({ operationId });
    } catch (error: unknown) {
      captureException(error, { source: "billing-checkout-operation-create" });
      setCheckoutClientError(
        userFacingErrorMessage(error, "Checkout could not be started in this browser."),
      );
    }
  }

  const hiddenSections = layout.hidden;

  return (
    <PageLayout title="Settings">
      <div className="space-y-3">
        <label className="block">
          <span className="sr-only">Search settings</span>
          <input
            type="search"
            aria-label="Search settings"
            value={categorySearch}
            onChange={(event) => setCategorySearch(event.target.value)}
            placeholder="Search settings"
            className="w-full rounded-lg border border-border bg-surface-solid px-3 py-2 text-sm text-foreground outline-none transition-colors placeholder:text-subtle focus:border-accent"
          />
        </label>
        <div
          role="tablist"
          aria-label="Settings categories"
          className="flex gap-1 overflow-x-auto rounded-lg border border-border bg-surface/60 p-1 scrollbar-hide"
        >
          {visibleCategories.map((category) => {
            const isActive = activeCategory === category.id;
            return (
              <button
                key={category.id}
                id={`settings-tab-${category.id}`}
                type="button"
                role="tab"
                aria-selected={isActive}
                aria-controls="settings-panel"
                onClick={() => {
                  setCategorySearch("");
                  void navigate({
                    search: (previous) => ({ ...previous, tab: category.id }),
                  });
                }}
                className={`min-w-fit flex-1 rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-accent/15 text-foreground"
                    : "text-subtle hover:bg-surface-hover hover:text-foreground"
                }`}
              >
                {category.label}
              </button>
            );
          })}
        </div>
        {visibleCategories.length === 0 ? (
          <p className="text-sm text-subtle">No settings categories match “{categorySearch}”.</p>
        ) : null}
      </div>

      <div
        id="settings-panel"
        role="tabpanel"
        aria-labelledby={activeCategory ? `settings-tab-${activeCategory}` : undefined}
        className="space-y-6 sm:space-y-7"
      >
        {activeCategory === "billing" ? (
          <PageSection title="Billing" subtitle="Manage subscription and access window">
            {billingStatus.isLoading ? (
              <p className={`${billingRegionClassName} text-sm text-subtle`}>
                Loading subscription status...
              </p>
            ) : billingStatus.error ? (
              <p className={`${billingRegionClassName} text-sm text-red-400`}>
                {userFacingErrorMessage(billingStatus.error)}
              </p>
            ) : billingStatus.data ? (
              <div className={`${billingRegionClassName} space-y-3`}>
                <p className="text-sm text-subtle">
                  {billingStatus.data.access.kind === "limited"
                    ? `Your access is limited to your most recent 7 days (${getRecentWeekLabel(
                        billingStatus.data.access.startDate,
                        billingStatus.data.access.endDateExclusive,
                      )}).`
                    : billingStatus.data.access.reason === "app_store_subscription"
                      ? "Full access is enabled."
                      : "You currently have full access to your data."}
                </p>
                <div className="space-y-1">
                  {billingStatus.data.access.reason === "stripe_subscription" &&
                  billingStatus.data.stripeSubscriptionStatus ? (
                    <p className="text-xs text-muted">
                      Stripe subscription status: {billingStatus.data.stripeSubscriptionStatus}
                    </p>
                  ) : null}
                </div>
                <div className="flex flex-wrap gap-2">
                  {!billingStatus.data.hasFullAccess && (
                    <button
                      type="button"
                      onClick={handleCheckout}
                      disabled={checkoutSessionMutation.isPending}
                      className="px-3 py-2 rounded bg-accent text-on-accent hover:bg-accent/90 disabled:opacity-50 transition-colors cursor-pointer"
                    >
                      {checkoutSessionMutation.isPending
                        ? "Opening checkout..."
                        : "Subscribe to Full Access"}
                    </button>
                  )}
                  {billingStatus.data.canManageBilling && (
                    <button
                      type="button"
                      onClick={() => portalSessionMutation.mutate()}
                      disabled={portalSessionMutation.isPending}
                      className="px-3 py-2 rounded border border-border-strong text-foreground hover:bg-surface-hover disabled:opacity-50 transition-colors cursor-pointer"
                    >
                      {portalSessionMutation.isPending
                        ? "Opening billing portal..."
                        : "Manage Billing"}
                    </button>
                  )}
                </div>
                {checkoutSessionMutation.error ? (
                  <p className="text-sm text-red-400">
                    {userFacingErrorMessage(checkoutSessionMutation.error)}
                  </p>
                ) : null}
                {checkoutClientError ? (
                  <p className="text-sm text-red-400">{checkoutClientError}</p>
                ) : null}
                {portalSessionMutation.error ? (
                  <p className="text-sm text-red-400">
                    {userFacingErrorMessage(portalSessionMutation.error)}
                  </p>
                ) : null}
              </div>
            ) : (
              <div className={billingRegionClassName} />
            )}
          </PageSection>
        ) : null}

        {activeCategory === "data-sources" ? (
          <PageSection title="Data Sources" subtitle="Connect and manage health data providers">
            <DataSourcesPanel />
          </PageSection>
        ) : null}

        {activeCategory === "account" ? (
          <PageSection
            title="Linked Accounts"
            subtitle="Manage login methods linked to your account"
          >
            <LinkedAccountsPanel />
          </PageSection>
        ) : null}

        {activeCategory === "account" ? (
          <PageSection title="Password">
            <PasswordSettingsPanel />
          </PageSection>
        ) : null}

        {activeCategory === "data-sources" ? (
          <PageSection title="Pair your Zepp app" subtitle="Enter the code shown in Zepp">
            <ZeppPairingPanel />
          </PageSection>
        ) : null}

        {activeCategory === "advanced" ? (
          <PageSection
            title="Developer integrations"
            subtitle="Register and manage clients that write data through the external API"
          >
            <Link
              to="/developer-integrations"
              className="inline-flex rounded border border-border px-3 py-2 text-sm text-foreground transition-colors hover:bg-surface-hover"
            >
              Manage developer integrations
            </Link>
          </PageSection>
        ) : null}

        {activeCategory === "advanced" ? (
          <PageSection
            title="Model Context Protocol (MCP)"
            subtitle="Connect remote MCP clients and manage access tokens"
          >
            <McpTokensPanel />
          </PageSection>
        ) : null}

        {activeCategory === "models-units" ? (
          <PageSection title="Units">
            <UnitSystemToggle />
          </PageSection>
        ) : null}

        {activeCategory === "models-units" ? (
          <PageSection
            title="Climbing grades"
            subtitle="Choose the grade systems used for boulders and routes"
          >
            <ClimbingGradeSystemToggle />
          </PageSection>
        ) : null}

        {activeCategory === "medications" ? (
          <PageSection title="Medication Doses" subtitle="Review imported medication dose events">
            <MedicationDoseEventsPanel />
          </PageSection>
        ) : null}

        {activeCategory === "advanced" ? (
          <PageSection
            title="Dashboard Layout"
            subtitle="Manage hidden sections and reset layout to defaults"
          >
            <div className="space-y-4">
              {hiddenSections.length > 0 ? (
                <div className="space-y-2">
                  <h3 className="text-xs font-medium text-subtle uppercase">Hidden Sections</h3>
                  <ul className="space-y-1">
                    {hiddenSections.map((id) => (
                      <li
                        key={id}
                        className="flex items-center justify-between py-1.5 px-2 rounded bg-accent/10"
                      >
                        <span className="text-sm text-foreground">{SECTION_LABELS[id] ?? id}</span>
                        <button
                          type="button"
                          onClick={() => toggleHidden(id)}
                          className="text-xs text-blue-400 hover:text-blue-300 transition-colors cursor-pointer"
                        >
                          Show
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <p className="text-sm text-subtle">No hidden sections</p>
              )}

              <button
                type="button"
                onClick={resetLayout}
                className="text-xs text-muted hover:text-foreground border border-border-strong rounded px-3 py-1.5 transition-colors cursor-pointer"
              >
                Reset to Default
              </button>
            </div>
          </PageSection>
        ) : null}

        {activeCategory === "models-units" ? (
          <PageSection
            title="Algorithm Personalization"
            subtitle="Parameters are automatically learned from your data to improve accuracy"
          >
            <PersonalizationPanel />
          </PageSection>
        ) : null}

        {activeCategory === "privacy-export" ? (
          <PageSection title="Data Export" subtitle="Download all your data">
            <ExportPanel />
          </PageSection>
        ) : null}

        {activeCategory === "account" ? (
          <PageSection title="Contact support" subtitle="We'll reply by email">
            <Link
              to="/support"
              className="inline-flex items-center gap-2 rounded border border-border-strong px-4 py-2 text-sm text-foreground transition-colors hover:bg-accent/10"
            >
              Send a message
            </Link>
          </PageSection>
        ) : null}

        {activeCategory === "privacy-export" ? (
          <PageSection
            title="Danger Zone"
            subtitle="Permanently close your account and delete your Dofek data"
            card={false}
          >
            <div className="rounded-lg border border-red-900/60 bg-surface-solid p-4 space-y-3">
              <AccountErasurePanel />
            </div>
          </PageSection>
        ) : null}
      </div>
    </PageLayout>
  );
}
